import { z } from 'zod';
import { LevelDefinition } from 'libs/end-to-end/src/types/databases/dimensions/level-definition';
import { PayBenchmarkingCompensationFigures } from 'libs/end-to-end/src/pay-benchmarking';
import { PoolReadinessConfidenceLevel } from 'libs/end-to-end/src/confidence';

export const CopilotComparisonOperator = z
  .enum([
    'lessThan',
    'lessThanOrEqual',
    'greaterThan',
    'greaterThanOrEqual',
    'equal',
    'twoSided',
  ])
  .describe('CopilotComparisonOperator');

export type CopilotComparisonOperator = z.infer<
  typeof CopilotComparisonOperator
>;

export const CopilotExplanationType = z
  .enum(['factual', 'alternative'])
  .describe('CopilotExplanationType');
export type CopilotExplanationType = z.infer<typeof CopilotExplanationType>;

export const CopilotGapType = z
  .enum(['inTarget', 'belowTarget', 'mismatch'])
  .describe('CopilotGapType');
export type CopilotGapType = z.infer<typeof CopilotGapType>;

export const CopilotRuleGap = z
  .object({
    type: CopilotGapType,
    value: z.number(),
  })
  .describe('CopilotRuleGap');
export type CopilotRuleGap = z.infer<typeof CopilotRuleGap>;

export const CopilotFeatureTarget = z
  .object({
    comparison_operator: CopilotComparisonOperator,
    cut_values: z.array(z.string()),
  })
  .describe('CopilotFeatureTarget');
export type CopilotFeatureTarget = z.infer<typeof CopilotFeatureTarget>;

export const CopilotTrait = z
  .object({
    feature_name: z.string(),
    current_feature_value: z.string(),
    feature_target: CopilotFeatureTarget,
    gap: CopilotRuleGap,
    explanation_type: CopilotExplanationType,
    trait_importance: z.number(),
    impact: z.number(),
  })
  .describe('CopilotTrait');

export type CopilotTrait = z.infer<typeof CopilotTrait>;

/**
 * Which way is better for a numeric characteristic, derived from the target's comparison operator.
 * Absent for a two-sided band and for a categorical match, where "better" has no direction.
 */
export const ApplicantPathwayCreditedDirection = z
  .enum(['higher', 'lower'])
  .describe('ApplicantPathwayCreditedDirection');
export type ApplicantPathwayCreditedDirection = z.infer<
  typeof ApplicantPathwayCreditedDirection
>;

export const ApplicantPathwayDisplayValueFormat = z
  .enum(['count', 'durationYears', 'verbatim'])
  .describe('ApplicantPathwayDisplayValueFormat');
export type ApplicantPathwayDisplayValueFormat = z.infer<
  typeof ApplicantPathwayDisplayValueFormat
>;

export const ApplicantPathwayCharacteristic = z
  .object({
    feature: z.string(),
    /** Percentile points the model attributes to THIS applicant for this feature. */
    applicantImpact: z.number().nullish(),
    /** The characteristic's impact on the pathway, identical for everyone on it. */
    pathwayImpact: z.number().nullish(),
    /**
     * The applicant's own value for the feature.
     */
    applicantValue: z.union([z.number(), z.string()]).nullish(),
    /**
     * True when the characteristic is normally suppressed for distinguishing nobody, but was
     * re-admitted because the model credits it materially for this applicant.
     */
    readmitted: z.boolean(),
    /**
     * The pathway's cut for this characteristic, so `applicantValue` can be read against something.
     * Identical for everyone on the pathway. For a top quality it is the range the applicant's value
     * falls in; for a development area it is the cut they would need to reach.
     */
    target: CopilotFeatureTarget.nullish(),
    /**
     * Which way is better, derived from `target.comparison_operator` rather than stored
     * independently, so the two cannot disagree.
     */
    creditedDirection: ApplicantPathwayCreditedDirection.nullish(),
    /**
     * Preface word(s) the bundle's display spec chose for this chip's section, composed by the
     * renderer with the schema display name ("Shorter" + "Most recent tenure" ->
     * "Shorter most recent tenure"). Null when no spec is attached or it does not preface this chip.
     */
    displayPrefix: z.string().nullish(),
    /**
     * Full label override from the display spec ("Role mismatch"); wins over `displayPrefix` and the
     * schema display name.
     */
    displayLabel: z.string().nullish(),
    /**
     * True when the display spec suppresses this chip in its section. Kept on the payload rather than
     * dropped at inference time so the renderer can suppress BEFORE its bullet cap and backfill with
     * the next-ranked chip. Distinct from an absent spec, which pydantic would also emit as null on
     * `displayPrefix`/`displayLabel`.
     */
    displaySuppressed: z.boolean().nullish(),
    /** How to render `applicantValue` beside the label; absent = do not show the value. */
    displayValueFormat: ApplicantPathwayDisplayValueFormat.nullish(),
    /**
     * Chips sharing a key collapse to the first by rank ("role", "shift"), so three role mismatches
     * read as one bullet.
     */
    dedupKey: z.string().nullish(),
  })
  .describe('ApplicantPathwayCharacteristic');
export type ApplicantPathwayCharacteristic = z.infer<
  typeof ApplicantPathwayCharacteristic
>;

/**
 * Section labels from the bundle's display spec. Their presence on an explanation is what switches
 * the text renderer to the spec-driven layout; without them it renders the legacy format.
 */
export const ApplicantPathwayDisplayLabels = z
  .object({
    topQualitiesLabel: z.string(),
    developmentAreasLabel: z.string(),
    pathwayNameLabel: z.string(),
  })
  .describe('ApplicantPathwayDisplayLabels');
export type ApplicantPathwayDisplayLabels = z.infer<
  typeof ApplicantPathwayDisplayLabels
>;

/**
 * An applicant's placement on a success pathway and what that pathway says about them. Null whenever
 * the model carries no pathways, the applicant could not be placed on one, or the pathway pass failed.
 */
export const ApplicantPathwayExplanation = z
  .object({
    pathwayId: z.number(),
    pathwayName: z.string().nullish(),
    topQualities: ApplicantPathwayCharacteristic.array(),
    developmentAreas: ApplicantPathwayCharacteristic.array(),
    /** Features with no value for this applicant, so they could not be assessed either way. */
    notEvaluable: z.string().array(),
    missingFeatureGroups: z.string().array(),
    /** Present exactly when the bundle carried a `pathway_display_spec` artifact. */
    displayLabels: ApplicantPathwayDisplayLabels.nullish(),
  })
  .describe('ApplicantPathwayExplanation');
export type ApplicantPathwayExplanation = z.infer<
  typeof ApplicantPathwayExplanation
>;

export const CopilotReadinessScoreExplanation = z
  .object({
    calibrated_readiness_score: z.number(),
    traits: z.array(CopilotTrait),
  })
  .describe('CopilotReadinessScoreExplanation');

export type CopilotReadinessScoreExplanation = z.infer<
  typeof CopilotReadinessScoreExplanation
>;

export const PoolSubject = z.object({
  id: z.string(),
  name: z.string(),
  jobTitle: z.string(), // this is the current job title for promotion co-pilot
  jobTitleGoal: z.string().nullish(), // this is used by promotion copilot since the goal is different than the current value above
  experienceInYears: z.number().nullable().optional(),
  education: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
  race: z.string().nullable().optional(),
  gender: z.string().nullable().optional(),
  confidenceLevel: PoolReadinessConfidenceLevel,
  isOver40: z.boolean(),
  compensationFigures: PayBenchmarkingCompensationFigures.nullish(),
  explanation: CopilotReadinessScoreExplanation.nullish(),
});
export type PoolSubject = z.infer<typeof PoolSubject>;

// This is used for when we produce the CSV export for promotion copilot after combining results.
export const ExportedCsvPoolSubject = PoolSubject.extend({
  explanationText: z.string().nullish(),
  // We add this on because we export in CSV format, and therefore it can not be top level like in the CopilotDataPoint.
  readinessRating: z.number().min(0).max(100),
});

export type ExportedCsvPoolSubject = z.infer<typeof ExportedCsvPoolSubject>;

export const CopilotDataPoint = z.object({
  numberOfEmployees: z.number(),
  readinessRating: z.number().min(0).max(100),
  subjects: z.array(PoolSubject),
});
export type CopilotDataPoint = z.infer<typeof CopilotDataPoint>;

export const CopilotOutput = z.array(CopilotDataPoint);
export type CopilotOutput = z.infer<typeof CopilotOutput>;

export const CopilotType = z.enum([
  'hiring',
  'pay-benchmarking',
  'pay-correction',
  'promotion',
]);

export type CopilotType = z.infer<typeof CopilotType>;

export const CopilotTypes = Object.values(CopilotType.Values);

export interface SlatePoolSubject extends PoolSubject {
  isInSlate: boolean;
  readinessRating: number;
  levelDefinitions: LevelDefinition[];
  sortableTotalCompensation: number;
}

export interface ClusteredDataPoint {
  minimumReadiness: number;
  maximumReadiness: number;
  averageReadinessWhole: number;
  numberOfEmployees: number;
  numberOfSubjects: number;
}

export interface SlateCopilotDataPoint {
  numberOfSubjects: number;
  numberOfEmployees: number;
  readinessRating: number;
  subjects: SlatePoolSubject[];
}

export const CopilotAutoMLConfigurationPreset = z
  .enum(['no_automl', 'model_only', 'permutation_fs', 'ensemble_fs', 'custom'])
  .describe('CopilotAutoMLConfigurationPreset');

export type CopilotAutoMLConfigurationPreset = z.infer<
  typeof CopilotAutoMLConfigurationPreset
>;

export const CopilotAutoMLConfigurationTaskType = z
  .enum(['classification', 'regression'])
  .describe('CopilotAutoMLConfigurationTaskType');

export type CopilotAutoMLConfigurationTaskType = z.infer<
  typeof CopilotAutoMLConfigurationTaskType
>;

export const CopilotAutoMLConfigurationApproachType = z
  .enum(['automl', 'random_forest', 'xgboost'])
  .describe('CopilotAutoMLConfigurationApproachType');

export type CopilotAutoMLConfigurationApproachType = z.infer<
  typeof CopilotAutoMLConfigurationApproachType
>;

export const CopilotAutoMLConfigurationFeatureSelection = z
  .object({
    naColumnFilter: z.number(),
    removeNaForSelection: z.boolean(),
    useSelection: z.boolean(),
    selectedTopPercentile: z.number(),
  })
  .describe('CopilotAutoMLConfigurationFeatureSelection');

export type CopilotAutoMLConfigurationFeatureSelection = z.infer<
  typeof CopilotAutoMLConfigurationFeatureSelection
>;

export const CopilotAutoMLConfigurationAutoMLSpecifics = z
  .object({
    huberLossDelta: z.number(),
    robustnessPenalty: z.number().min(0).max(1),
    useRay: z.boolean(),
  })
  .describe('CopilotAutoMLConfigurationAutoMLSpecifics');

export type CopilotAutoMLConfigurationAutoMLSpecifics = z.infer<
  typeof CopilotAutoMLConfigurationAutoMLSpecifics
>;

export const CopilotAutoMLNoAutoMLConfiguration = z
  .object({
    preset: z.literal('no_automl'),
  })
  .describe('CopilotAutoMLNoAutoMLConfiguration');

export type CopilotAutoMLNoAutoMLConfiguration = z.infer<
  typeof CopilotAutoMLNoAutoMLConfiguration
>;

export const CopilotAutoMLModelOnlyConfiguration = z
  .object({
    preset: z.literal('model_only'),
  })
  .describe('CopilotAutoMLModelOnlyConfiguration');

export type CopilotAutoMLModelOnlyConfiguration = z.infer<
  typeof CopilotAutoMLModelOnlyConfiguration
>;

export const CopilotAutoMLPermutationFsConfiguration = z
  .object({
    preset: z.literal('permutation_fs'),
  })
  .describe('CopilotAutoMLPermutationFsConfiguration');

export type CopilotAutoMLPermutationFsConfiguration = z.infer<
  typeof CopilotAutoMLPermutationFsConfiguration
>;

export const CopilotAutoMLEnsembleFsConfiguration = z
  .object({
    preset: z.literal('ensemble_fs'),
  })
  .describe('CopilotAutoMLEnsembleFsConfiguration');

export type CopilotAutoMLEnsembleFsConfiguration = z.infer<
  typeof CopilotAutoMLEnsembleFsConfiguration
>;

export const CopilotAutoMLCustomConfiguration = z
  .object({
    preset: z.literal('custom'),
    taskType: CopilotAutoMLConfigurationTaskType,
    modelApproach: CopilotAutoMLConfigurationApproachType,
    targetEncoding: z.boolean(),
    featureSelection: CopilotAutoMLConfigurationFeatureSelection,
    autoMlSpecifics: CopilotAutoMLConfigurationAutoMLSpecifics,
  })
  .describe('CopilotAutoMLCustomConfiguration');

export type CopilotAutoMLCustomConfiguration = z.infer<
  typeof CopilotAutoMLCustomConfiguration
>;

export const CopilotAutoMLConfiguration = z
  .union([
    CopilotAutoMLNoAutoMLConfiguration,
    CopilotAutoMLModelOnlyConfiguration,
    CopilotAutoMLPermutationFsConfiguration,
    CopilotAutoMLEnsembleFsConfiguration,
    CopilotAutoMLCustomConfiguration,
  ])
  .describe('CopilotAutoMLConfiguration');

export type CopilotAutoMLConfiguration = z.infer<
  typeof CopilotAutoMLConfiguration
>;

export const CopilotAutoMLDefaultCustomConfigurationNaColumnFilter = 0.6;
export const CopilotAutoMLDefaultCustomConfigurationSelectedTopPercentile = 0.5;
export const CopilotAutoMLDefaultCustomConfigurationHuberLossDelta = 10;
export const CopilotAutoMLDefaultCustomConfigurationRobustnessPenalty = 0.3;

export const CopilotAutoMLDefaultCustomConfiguration: CopilotAutoMLCustomConfiguration =
  {
    preset: 'custom',
    taskType: 'regression',
    modelApproach: 'xgboost',
    targetEncoding: false,
    featureSelection: {
      naColumnFilter: CopilotAutoMLDefaultCustomConfigurationNaColumnFilter,
      removeNaForSelection: false,
      useSelection: false,
      selectedTopPercentile:
        CopilotAutoMLDefaultCustomConfigurationSelectedTopPercentile,
    },
    autoMlSpecifics: {
      huberLossDelta: CopilotAutoMLDefaultCustomConfigurationHuberLossDelta,
      robustnessPenalty:
        CopilotAutoMLDefaultCustomConfigurationRobustnessPenalty,
      useRay: false,
    },
  };
